import cn from 'classnames';
import * as React from 'react';
import { useState } from 'react';
import { Search, PreferencesApi } from '@weddingspot/ws-api-client';
import { LocalStorage, StorableDataKeys, WSAppContext } from '@weddingspot/ws-core-components';
import { toUSD } from '@weddingspot/ws-core-components';
import * as analytics from '@weddingspot/ws-analytics';
import { useFrontendPreferences } from './hooks';

const { AnalyticsEvent, logEvent } = analytics;

import { Button } from '@cvent/carina/components/Button';
import { InfoIcon, HomeIcon, UtencilsIcon, DollarSignIcon } from '@cvent/carina/components/Icon';

const _styles = require('./BudgetBanner.scss');

export interface BudgetBannerProps {
    venue?: Search.SearchResult | null;
}

export const BudgetBanner = React.memo((props: BudgetBannerProps) => {
    const [isHidden, setIsHidden] = useState(true);
    const { userIsAuthenticated, user } = React.useContext(WSAppContext);
    const frontendPreferences = useFrontendPreferences();

    React.useEffect(() => {
        let bannerSeen;
        if (userIsAuthenticated && frontendPreferences) {
            const pref = frontendPreferences.find((pref) => pref.key === '14');
            bannerSeen = pref ? Boolean(pref.value) : false;
        } else {
            bannerSeen = LocalStorage.getItem(StorableDataKeys.BUDGET_ESTIMATE_BANNER_SEEN);
        }

        if (!bannerSeen) {
            setIsHidden(false);
            LocalStorage.setItem(StorableDataKeys.BUDGET_ESTIMATE_BANNER_SEEN, false);
        } else {
            LocalStorage.setItem(StorableDataKeys.BUDGET_ESTIMATE_BANNER_SEEN, true);
        }
    }, []);

    const setFrontendPreference = () => {
        const data = {
            key: '14',
            is_active: true,
            value: 1,
            description: 'Is Budget Estimate Banner Seen',
            user_id: user?.id,
        };
        PreferencesApi.update(data).then((response) => {});
    };

    const handleCTAClick = (e: React.SyntheticEvent) => {
        LocalStorage.setItem(StorableDataKeys.BUDGET_ESTIMATE_BANNER_SEEN, true);
        setIsHidden(true);
        if (userIsAuthenticated) {
            setFrontendPreference();
        }
        logEvent(AnalyticsEvent.EVTNM_BUDGET_ESTIMATE_BANNER_SEEN, {});
    };

    return (
        <div className={cn(_styles.wrapper, { [_styles.hidden]: isHidden})}>
            <div className={cn(_styles.title)}>
                <InfoIcon size='m' color='#881155' /> Did you know?
            </div>

            <div className={cn(_styles.description)}>We provide budget estimates for venues to help you make an informed decision.</div>

            <div className={cn(_styles.includesInfo)}>
                <b>Budget Estimate Includes:</b>
                <ul>
                    <li>
                        <HomeIcon /> Venue Rental Costs
                    </li>
                    <li>
                        <UtencilsIcon /> Food & Beverage Costs
                    </li>
                    <li>
                        <DollarSignIcon /> Taxes and Other Fees
                    </li>
                </ul>
            </div>

            <div className={cn(_styles.description)}>
                Our Spot Estimate tool helps plan for a budget that works best for you. Try it by pricing out a venue you like!
            </div>

            {props.venue && props.venue.starting_at && props.venue.max_capacity && (
                <div className={_styles.budgetRange}>
                    <p>
                        <b>Example: {props.venue.name}</b>
                    </p>
                    <div>
                        Starting at <b>{toUSD(props.venue.starting_at)} </b>
                        for <b>{props.venue.max_capacity}</b> Guests
                    </div>
                </div>
            )}

            <div className={_styles.bottomRow}>
                <div className={_styles.buttonContainer}>
                    <Button className={_styles.ctaButton} text={'Got It!'} onClick={handleCTAClick} size='l' />
                </div>
            </div>
        </div>
    );
});
